import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import styled from "../../../component/commonCard/style.module.css";
import KpiCard from "../../../component/commonCard/parts/KpiCard";
import BreakdownBars from "../../../component/commonCard/parts/BreakdownBars";
import { COPPER, TEAL, GREEN, MAGENTA } from "../../../component/commonCard/parts/theme";
import NotAvailableYet from "../NotAvailableYet";
import TrendChart from "../TrendChart";
import ExportButtons from "../ExportButtons";
import { getContentDrilldown } from "../../../api/HttpApis";
import { showToast } from "../../../utils/Toastify/Toastify";

// Bunny's statistics endpoint returns chart data as an object keyed by
// UTC interval (not a pre-sorted array), so normalize defensively -- handles
// both an object map and an array, same defensive approach used for the
// heatmap on the backend (AnalyticsService.retentionCurve).
const normalizeChart = (chart, valueKey) => {
  if (!chart) return [];
  const entries = Array.isArray(chart)
    ? chart.map((v, i) => [String(i), v])
    : Object.entries(chart);
  return entries
    .map(([date, value]) => ({ date: date.slice(0, 10), [valueKey]: Number(value) || 0 }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
};

const ContentDrilldown = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const backParams = new URLSearchParams(searchParams);
  backParams.set("tab", "Content");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await getContentDrilldown(slug);
        if (!cancelled) setData(res?.data?.data || null);
      } catch (err) {
        // A title with no plays yet is an expected 404 from the backend, not
        // a failure -- only toast for real errors so it doesn't look broken.
        if (!cancelled && err?.response?.status !== 404) {
          showToast(err?.response?.data?.message || "Failed to load content drilldown", "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Absolute path, not relative "..": this route is registered as its own
  // flat "analytics-dashboard/content/:slug" path rather than a child of
  // "analytics-dashboard", so React Router resolves ".." against the route
  // hierarchy, not the URL, and it doesn't reliably land back here.
  const backLink = (
    <Link to={`/mainlayout/analytics-dashboard?${backParams.toString()}`} className={styled.drilldownBackLink}>
      <span aria-hidden="true">←</span> Back to Content
    </Link>
  );

  if (loading) return <div className={styled.loading}>Loading title analytics…</div>;
  if (!data) {
    return (
      <div className={styled.chartCardHead} style={{ marginBottom: 12 }}>
        <div>
          {backLink}
          <p className={styled.emptyHint} style={{ marginTop: 12 }}>This title has no recorded playback data yet.</p>
        </div>
      </div>
    );
  }

  const retention = data.retention_curve || {};
  const bunny = data.bunny_stats || {};
  const bunnyViews = normalizeChart(bunny.viewsChart, "views");
  const bunnyWatchTime = normalizeChart(bunny.watchTimeChart, "watch_time_seconds");
  const exportRows = [
    { Metric: "Views (Starts)", Value: data.starts },
    { Metric: "Completions", Value: data.completes },
    { Metric: "Unique Viewers", Value: data.unique_viewers },
    { Metric: "Completion Rate %", Value: data.completion_percent },
    ...(bunny.available && bunny.engagementScore != null ? [{ Metric: "Bunny Engagement Score", Value: bunny.engagementScore }] : []),
    ...(retention.available ? retention.buckets.map((b) => ({ Metric: `Retention ${b.bucket}`, Value: b.avg_intensity })) : []),
    ...(data.device_breakdown || []).map((d) => ({ Metric: `Plays on ${d.platform}`, Value: d.plays })),
  ];

  return (
    <>
      <div className={styled.chartCardHead} style={{ marginBottom: 12 }}>
        <div>
          {backLink}
          <h2 className={styled.drilldownTitle} style={{ margin: "6px 0 0" }}>{data.title}</h2>
          {(data.series || data.season) && (
            <p className={styled.emptyHint} style={{ marginTop: 4 }}>
              {[data.series?.name, data.season?.name].filter(Boolean).join(" · ")}
            </p>
          )}
          {/* This page's query has no date filter and always reflects all-time
              data, but it's reached from underneath the global date range
              picker, so say so explicitly rather than let it look scoped. */}
          <p className={styled.emptyHint} style={{ marginTop: 4 }} title="This title's stats aren't scoped by the dashboard's date range filter.">
            Showing all-time data (not scoped by the date range filter)
          </p>
        </div>
      </div>

      <ExportButtons rows={exportRows} filename={`content-${data.slug}`} />

      <div className={styled.kpiRow}>
        <div title="Number of times playback began for this title.">
          <KpiCard label="Views (Starts)" value={data.starts.toLocaleString()} accent={COPPER} />
        </div>
        <div title="Number of times this title was watched to the end.">
          <KpiCard label="Completions" value={data.completes.toLocaleString()} accent={GREEN} />
        </div>
        <div title="Distinct users who started this title.">
          <KpiCard label="Unique Viewers" value={data.unique_viewers.toLocaleString()} accent={TEAL} />
        </div>
        <div title="Completions ÷ starts for this title.">
          <KpiCard label="Completion Rate" value={`${data.completion_percent}%`} accent={MAGENTA} />
        </div>
        {bunny.available && bunny.engagementScore != null && (
          <div title="Bunny's own 0–100 retention indicator, computed from real CDN playback.">
            <KpiCard label="Bunny Engagement Score" value={`${bunny.engagementScore}`} accent={GREEN} />
          </div>
        )}
      </div>

      <div className={styled.chartsRow}>
        {retention.available ? (
          <BreakdownBars
            title="Viewer Retention"
            rows={retention.buckets}
            nameKey="bucket"
            valueKey="avg_intensity"
            formatValue={(v) => `${v}`}
            color={COPPER}
            emptyHint="No retention data yet."
          />
        ) : (
          <NotAvailableYet
            title="Viewer Retention"
            reason={retention.reason || "Retention data appears once Bunny has enough playback data for this title."}
          />
        )}

        <BreakdownBars
          title="Plays by Platform"
          rows={data.device_breakdown}
          nameKey="platform"
          valueKey="plays"
          color={TEAL}
          emptyHint="No platform breakdown recorded for this title yet."
        />
      </div>

      {bunny.available ? (
        <div className={styled.chartsRow}>
          <TrendChart
            title="Views Over Time (Bunny CDN)"
            data={bunnyViews}
            series={[{ key: "views", name: "Views", color: COPPER }]}
            emptyHint="No Bunny view-chart data for this title yet."
          />
          <TrendChart
            title="Watch Time Over Time (Bunny CDN)"
            data={bunnyWatchTime}
            series={[{ key: "watch_time_seconds", name: "Watch Time (s)", color: GREEN }]}
            emptyHint="No Bunny watch-time data for this title yet."
          />
        </div>
      ) : (
        <NotAvailableYet title="Bunny CDN Statistics" reason={bunny.reason || "No linked Bunny video for this title."} />
      )}
    </>
  );
};

export default ContentDrilldown;
